import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/typography'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  ListingCSVProcessor,
  buildListingCsvTemplate,
  type ListingCSVProcessingResult,
} from '@/lib/csv-upload/listing-csv-processor'
import { downloadTextFile } from '@/lib/csv-upload/utils/download-utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { listingsApiClient, ApiError, integrationsClient } from '@/lib/api'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { INTEGRATIONS } from '@super/shared-types'
// Import the specific component (not the ./shared barrel) so the listings
// dialog doesn't transitively pull in the PMS-transformer chain it never uses.
import { CSVFileUploader } from './shared/CSVFileUploader'

interface ListingCSVUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete?: () => void
  preloadedFile: File | null
  onFileChange: (file: File | null) => void
  /**
   * Connected integrations whose LISTINGS sync is currently running. Uploading
   * a CSV replaces all listings, so we stop these first (and warn the user).
   */
  activeListingsIntegrations?: string[]
  /** Whether a scheduled URL sync is currently active. */
  activeUrlSync?: boolean
}

/** Resolve an integration display name (e.g. RENTVINE -> "Rentvine"). */
function integrationName(type: string): string {
  const match = Object.values(INTEGRATIONS).find(config => config.TYPE === type)
  return match?.NAME ?? type
}

/** Turn an upload failure into a message that names the actual cause. */
function formatUploadError(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as
      | {
          message?: string
          errors?: Array<{ field?: string; message: string }>
        }
      | undefined
    if (body?.errors?.length) {
      return body.errors
        .slice(0, 5)
        .map(e => (e.field ? `${e.field}: ${e.message}` : e.message))
        .join('; ')
    }
    if (body?.message) return `${body.message} (HTTP ${error.status})`
    return error.message
  }
  return error instanceof Error ? error.message : 'Failed to upload listings'
}

function downloadTemplate() {
  downloadTextFile('listings-template.csv', buildListingCsvTemplate())
}

export function ListingCSVUploadDialog({
  open,
  onOpenChange,
  onUploadComplete,
  preloadedFile,
  onFileChange,
  activeListingsIntegrations = [],
  activeUrlSync = false,
}: ListingCSVUploadDialogProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<ListingCSVProcessingResult | null>(null)

  const handleFile = async (next: File | null) => {
    onFileChange(next)
    setResult(null)
    if (!next || !currentCompany) return
    setIsProcessing(true)
    try {
      const processed = await new ListingCSVProcessor(
        currentCompany.id,
      ).parseCSV(next)
      setResult(processed)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to process file',
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    setResult(null)
    setIsProcessing(false)
    setIsUploading(false)
    onFileChange(null)
    onOpenChange(false)
  }

  const handleApply = async () => {
    if (!result?.success || !currentCompany) return
    setIsUploading(true)
    try {
      // Uploading replaces all listings, so the previously active listing
      // source must stop first — otherwise it would keep re-populating. This is
      // a deliberate two-step flow (stop sync, then upload) rather than the
      // upload endpoint silently tearing sync down.
      for (const integrationType of activeListingsIntegrations) {
        if (isSuperAdmin) {
          await integrationsClient.stopAdminListingSync(
            currentCompany.id,
            integrationType,
          )
        } else {
          await integrationsClient.stopListingSync(integrationType)
        }
      }
      if (activeUrlSync) {
        if (isSuperAdmin) {
          await knowledgeApiClient.removeAdminListingUrlSync(currentCompany.id)
        } else {
          await knowledgeApiClient.removeListingUrlSync()
        }
      }

      const response = isSuperAdmin
        ? await listingsApiClient.uploadListingsCsvForCompany(
            currentCompany.id,
            result.listings,
          )
        : await listingsApiClient.uploadListingsCsv(result.listings)

      if (response?.success) {
        toast.success('Listings uploaded', {
          description: response.message,
          duration: 6000,
        })
        onUploadComplete?.()
        handleClose()
      } else {
        toast.error('Upload failed. Please try again.')
      }
    } catch (error) {
      toast.error('Upload failed', {
        description: formatUploadError(error),
        duration: 8000,
      })
    } finally {
      setIsUploading(false)
    }
  }

  const hasErrors = !!result && result.errors.length > 0
  const plural = (count: number) => (count === 1 ? '' : 's')

  // The API caps request bodies at 2MB; check the serialized payload up front so
  // a large file fails loudly here instead of as an opaque 413 on upload.
  const MAX_UPLOAD_BYTES = 1_900_000
  const payloadTooLarge = useMemo(() => {
    if (!result?.success) return false
    try {
      return (
        new Blob([JSON.stringify({ listings: result.listings })]).size >
        MAX_UPLOAD_BYTES
      )
    } catch {
      return false
    }
  }, [result])

  const canApply = result?.success === true && !payloadTooLarge && !isUploading

  // Name the listing source(s) that uploading will stop, so the warning is
  // specific ("Rentvine listing syncing") rather than generic.
  const activeSyncLabels = [
    ...activeListingsIntegrations.map(
      t => `${integrationName(t)} listing sync`,
    ),
    ...(activeUrlSync ? ['the scheduled URL sync'] : []),
  ]

  return (
    <Dialog
      open={open}
      onOpenChange={o => (o ? onOpenChange(true) : handleClose())}
    >
      <DialogContent className="gap-5">
        <DialogHeader>
          <DialogTitle>Upload listings CSV</DialogTitle>
          <DialogDescription>
            Uploading replaces all current listings. Listings are then managed
            by CSV upload.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {activeSyncLabels.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
              <div className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400">(i)</span>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    By uploading this CSV, {activeSyncLabels.join(' and ')} will
                    be disconnected.
                  </p>
                </div>
              </div>
            </div>
          )}

          <CSVFileUploader
            uploadedFile={preloadedFile}
            onFileChange={handleFile}
            disabled={isProcessing || isUploading}
          />

          {isProcessing && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing CSV…
            </div>
          )}

          {hasErrors && (
            <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
              <Text variant="muted-xs" className="font-medium text-destructive">
                {result!.errors.length} error{plural(result!.errors.length)} —
                fix the file and re-upload
              </Text>
              {result!.errors.slice(0, 6).map((error, i) => (
                <Text key={i} variant="muted-xs" className="text-destructive">
                  {error.row > 0 ? `Row ${error.row}: ` : ''}
                  {error.message}
                </Text>
              ))}
              {result!.errors.length > 6 && (
                <Text variant="muted-xs" className="text-destructive">
                  … and {result!.errors.length - 6} more
                </Text>
              )}
            </div>
          )}

          {payloadTooLarge && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
              <Text variant="muted-xs" className="text-destructive">
                This file is too large to upload in one request (limit ~2MB).
                Split it into smaller files (fewer rows) and upload them in
                sequence.
              </Text>
            </div>
          )}

          {result && !hasErrors && (
            <div className="space-y-1 rounded-md border px-3 py-2 text-sm">
              <p className="font-medium">
                {result.processedRows} listing{plural(result.processedRows)}{' '}
                ready to upload
              </p>
              {result.skipped > 0 && (
                <p className="text-muted-foreground">
                  {result.skipped} blank row{plural(result.skipped)} skipped
                </p>
              )}
              {!!result.duplicateCount && result.duplicateCount > 0 && (
                <p className="text-muted-foreground">
                  {result.duplicateCount} duplicate
                  {plural(result.duplicateCount)} in file — last occurrence kept
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="sm:mr-auto"
            onClick={downloadTemplate}
          >
            <Download className="mr-2 h-4 w-4" />
            Download template
          </Button>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!canApply} onClick={handleApply}>
            {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isUploading ? 'Uploading…' : 'Update listings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
